// File: `typescript/apps/front-end/src/main/navigation/useBackDismissesOverlay.ts`
import React from "react";

import { popOwnGuardEntry, pushGuardHistoryEntry } from "@/main/navigation/NavigationHistoryState";
import { registerNavigationDepartureObserver } from "@/main/navigation/NavigationLeaveGuardRegistry";
import { registerPopStateGuard } from "@/main/navigation/PopStateGuardRegistry";

/**
 * Make the browser/OS Back gesture dismiss a full-screen overlay instead of leaving the page.
 *
 * <p><b>Why this is a hook and not per-surface code (`NOTIFICATIONS-10`).</b> The notifications drawer,
 * the messages drawer and the media lightbox are the same overlay problem three times, and the register
 * that filed this row (theme T1) makes the general point: a fix delivered as a per-surface patch does
 * not generalise, and the sibling surface is where that shows up. `useBodyMediaLightbox` had the correct
 * implementation and nothing else could reuse it. This is that implementation, extracted, with the
 * imperative open/close verbs replaced by the `open` boolean a controlled overlay already has.
 *
 * <p><b>The extraction was only finished on 2026-09-20 (FA-11).</b> Until then `useBodyMediaLightbox`
 * kept its own copy of the lifecycle and `useThumbnailLightbox` was written with a second one, so the
 * two behaviours added to THIS file afterwards — the departure observer below (2026-08-27) and the
 * unmount cleanup at the bottom — existed in one of three places. The sentence above was therefore
 * true on the day it was written and false for the three weeks after it. Both media hooks call this
 * hook now; do not re-hand-roll it.
 *
 * <p><b>Rule 33 constraints, both load-bearing.</b> No module other than `NavigationHistoryState` may
 * write history, so the entry goes through `pushGuardHistoryEntry` — which STAMPS a fresh
 * `entryKind: "page"` state rather than spreading the current one. A spread entry would inherit
 * `"in-page"` and a still-matching page-instance id, and a declined pop would then do nothing at all.
 *
 * <p><b>The address bar is deliberately left alone.</b> An overlay opened from a page is a view state of
 * that page, not a route; the entry exists only to be consumed by Back.
 *
 * <p><b>The self-pop ref is not defensive coding.</b> Without it, the pop that our own pop verb
 * produces finds the guard disarmed, returns false, and hands the router a `"page"` entry to navigate
 * to — a full document fetch of the page the user is already on. That failure renders a page
 * indistinguishable from the correct one, so no appearance-based test can see it (rule 33).
 *
 * <p><b>The close path pops through `popOwnGuardEntry`, which verifies OWNERSHIP first (§13.5).</b>
 * This hook's central assumption — that the entry it pushed is still the entry on top — is false in one
 * reachable state, and on 2026-08-22 that state was reported from production. A notification row inside
 * the drawer is a plain `&lt;a&gt;`, not a `DrawerClose`, so nothing closes the drawer on click and the
 * soft navigation pushes `/messages?threadPublicId=…` ON TOP of the guard entry. The close path then
 * called `history.back()` unconditionally and ate the messages entry; the `selfPopPending` branch below
 * swallowed the resulting `popstate`, so the router deliberately did nothing. The member was left
 * looking at the messages page with the discussion's address in the bar and an empty conversation pane.
 *
 * <p>Reading the marker is the entire fix. It was already being written, and was documented right below
 * as "not read by the guard". When the pop is refused the hook disarms silently: the stray entry costs
 * one extra Back press, which is a great deal better than consuming a page the member navigated to.
 *
 * <p>Note the hook's ten existing cases could not see this — none of them navigates while the overlay
 * is open, which is the one state in which the assumption is false. Every frame in the failure looks
 * like a page that rendered correctly.
 */
export type UseBackDismissesOverlayOptions = {
    /** Whether the overlay is currently open. The hook arms on the false -> true edge. */
    open: boolean;
    /**
     * Close the overlay. Called ONLY from the pop guard — the hook never closes an overlay that the
     * member closed some other way, it only consumes the entry that close left behind.
     */
    onRequestClose: () => void;
    /**
     * A stable marker written onto the guard entry, and READ back by the close path to prove the entry
     * it is about to pop is still ours (§13.5). Arming is still tracked in a ref — the ref says "we
     * pushed", the marker says "our entry is still on top", and only the pair is sufficient.
     */
    marker: string;
};

export function useBackDismissesOverlay({ open, onRequestClose, marker }: UseBackDismissesOverlayOptions): void {
    // True while we own a sacrificial history entry that a Back must consume.
    const guardArmedRef = React.useRef(false);
    // True between calling `history.back()` ourselves and the pop it produces arriving.
    const selfPopPendingRef = React.useRef(false);
    const wasOpenRef = React.useRef(open);

    const closeRef = React.useRef(onRequestClose);
    closeRef.current = onRequestClose;
    // The unmount cleanup runs once, with the deps it closed over on mount — so it reads the marker
    // through a ref rather than pinning the mount-time value.
    const markerRef = React.useRef(marker);
    markerRef.current = marker;

    React.useEffect(() => registerPopStateGuard(() => {
        if (selfPopPendingRef.current) {
            selfPopPendingRef.current = false;
            guardArmedRef.current = false;
            return true;
        }
        if (!guardArmedRef.current) {
            return false; // not ours — normal navigation, unchanged
        }
        guardArmedRef.current = false;
        closeRef.current();
        return true; // handled: the overlay closed, nothing unmounts, no document is fetched
    }), []);

    React.useEffect(() => {
        const wasOpen = wasOpenRef.current;
        wasOpenRef.current = open;

        if (open && !wasOpen) {
            try {
                pushGuardHistoryEntry(window.location.href, { [marker]: true });
                guardArmedRef.current = true;
            } catch {
                // The overlay still works; only Back-to-close is lost.
                guardArmedRef.current = false;
            }
            return;
        }

        if (!open && wasOpen && guardArmedRef.current) {
            // Closed by the close button, Escape or the scrim: consume the entry we pushed, so Back
            // does not have to be pressed twice to leave the page afterwards.
            //
            // `selfPopPending` is armed only if the pop was actually ISSUED. Arming it for a refused
            // pop would leave the flag set until some unrelated later `popstate` arrived and got
            // swallowed — trading one wrong Back for a worse, delayed one.
            guardArmedRef.current = false;
            selfPopPendingRef.current = popOwnGuardEntry(marker);
        }
    }, [open, marker]);

    /**
     * `NOTIFICATIONS` / 2026-08-27 — a member who navigates OUT of an overlay leaves the overlay behind.
     *
     * <p>The rows inside these overlays are plain `&lt;a&gt;`s, so `EnhancedNavigation` turns a click
     * into a soft push: the document swaps, the header island never unmounts, and `open` is simply
     * still true over the destination. On a phone the drawer is full-bleed, so it covers the very page
     * it just sent the member to — which is the report, verbatim: *"the page changes BUT the sidebar
     * doesn't actually close, so then we can't really see what the notification was leading to."*
     *
     * <p>This is step 2 of the chain `AGENTS/frontend-navigation-and-history.md` reconstructed on
     * 2026-08-22 ("the row is a plain `<a>`, not a `DrawerClose`, so nothing closes the drawer"). That
     * work fixed steps 3-5 and left this one standing as scene-setting. It is also a defect.
     *
     * <p><b>It lives in the hook, not on the row.</b> Three overlays share the shape — this drawer, the
     * messages drawer and `ImageGridDisplayerDrawer` — and the register theme this hook was extracted
     * for (T1) is precisely that a per-surface patch does not generalise. A `DrawerClose` on the
     * notification row would fix one of the three and would still be blind to a programmatic navigation.
     *
     * <p><b>DISARM BEFORE CLOSING, AND DO NOT POP.</b> This is the whole correctness argument. The
     * ordinary close path consumes the guard entry with `popOwnGuardEntry`. If this observer only called
     * `closeRef.current()`, the `!open && wasOpen && guardArmedRef.current` effect below would fire and
     * issue a `history.back()` INTO a navigation that is already committing — racing a pop against the
     * push `runEnhancedNavigation` is about to perform. Clearing the ref first removes that branch's own
     * condition, which is the only reason it cannot run.
     *
     * <p>The stray guard entry that leaves behind is benign HERE, and it is worth saying why rather than
     * trusting it: the entry carries the URL of the page the overlay was opened on, and the destination
     * is pushed on top of it, so Back from the destination returns to that page. A document fetch rather
     * than a restore; the correct destination either way. Same trade the rulebook already takes — one
     * extra entry beats eating a page the member navigated to.
     */
    React.useEffect(() => registerNavigationDepartureObserver(() => {
        if (!wasOpenRef.current) {
            return; // nothing on screen to dismiss; leave history alone
        }
        guardArmedRef.current = false;
        closeRef.current();
    }), []);

    // Unmounting while open (a soft navigation under the overlay) must not leave a live entry behind
    // with nothing to consume it.
    React.useEffect(() => () => {
        if (!guardArmedRef.current) {
            return;
        }
        guardArmedRef.current = false;
        // Ownership is checked here too, and this is where it matters most: unmounting while open IS
        // the soft-navigation case, so the entry on top is very often the destination the member just
        // navigated to rather than ours.
        selfPopPendingRef.current = popOwnGuardEntry(markerRef.current);
    }, []);
}
